/**
 * Case package constants and product validate.
 * Shape, type tokens, and LLM rules live in ./schema.jsonc only.
 * Keys that mirror schema.jsonc are derived from CASE_TYPE_SCHEMA (no hand copy).
 */

import {
  collectMediaFieldNames,
  collectMediaUrls as collectMediaUrlsBase,
  collectRequiredPaths,
  formatError,
  listAllKeys,
  listObjectFields,
  listOptionalKeys,
  listRequiredKeys,
  loadTypeSchema,
  validateAgainstTypeSchema,
  type TypeSchema,
  type ValidationIssue,
  type ValidationResult,
} from '@peekie/case-spec'
import caseSchemaSource from './schema.jsonc' with { type: 'text' }

/** Raw schema.jsonc text (comments included) for LLM prompts. */
export const CASE_SCHEMA_SOURCE: string = caseSchemaSource

/** Parsed type-schema tree from schema.jsonc. */
export const CASE_TYPE_SCHEMA: TypeSchema = loadTypeSchema(caseSchemaSource)

/** Structural validator id for 0.1.x / 0.2.x (schema.jsonc). */
export const VALIDATOR_ID = 'current'
export const PAYPAL_INCOMING_RUNTIME = '0.2.105'

function schemaObjectAt(path: string): TypeSchema {
  let node: TypeSchema = CASE_TYPE_SCHEMA
  for (const part of path.split('.')) {
    const fields = listObjectFields(node)
    const match = fields.find((field) => field.key === part)
    if (!match) {
      throw new Error(`schema.jsonc missing path "${path}" (at "${part}")`)
    }
    node = match.schema
  }
  return node
}

const deviceAppsSchema = schemaObjectAt('device.apps')

/** Required top-level keys (from schema.jsonc; keys without ?). */
export const CASE_TOP_LEVEL_KEYS: readonly string[] =
  listRequiredKeys(CASE_TYPE_SCHEMA)

export type CaseTopLevelKey = (typeof CASE_TOP_LEVEL_KEYS)[number]

/** Optional top-level keys (from schema.jsonc; keys with ?). */
export const CASE_OPTIONAL_TOP_LEVEL_KEYS: readonly string[] = listOptionalKeys(
  CASE_TYPE_SCHEMA,
)

export type CaseOptionalTopLevelKey =
  (typeof CASE_OPTIONAL_TOP_LEVEL_KEYS)[number]

/**
 * Required nested paths under game / canon / device (derived from schema.jsonc).
 */
export const CASE_REQUIRED_NESTED: readonly string[] = (
  ['game', 'canon', 'device'] as const
).flatMap((root) => {
  const field = listObjectFields(CASE_TYPE_SCHEMA).find((f) => f.key === root)
  if (!field) return [] as string[]
  return collectRequiredPaths(field.schema, root)
})

/**
 * Legal device.apps keys from schema.jsonc.
 * Spotify/YouTube are fixed runtime content and do not take case bags.
 */
export const DEVICE_APP_KEYS: readonly string[] = listAllKeys(deviceAppsSchema)

export type DeviceAppKey = (typeof DEVICE_APP_KEYS)[number]

/**
 * Core apps every case must ship with non-empty mock life data.
 * From schema.jsonc required (non-?) keys under device.apps.
 */
export const REQUIRED_DEVICE_APP_KEYS: readonly string[] =
  listRequiredKeys(deviceAppsSchema)

export type RequiredDeviceAppKey = (typeof REQUIRED_DEVICE_APP_KEYS)[number]

/** Domain id → case JSON path (apps from schema; canon domains fixed). */
export const CASE_APP_PATHS: Record<string, string> = {
  ...Object.fromEntries(
    DEVICE_APP_KEYS.map((key) => [key, `device.apps.${key}`]),
  ),
  people: 'canon.people',
  world: 'canon.world',
  relationships: 'canon.relationships',
}

/** Field names that hold media URLs (derived: token media_url in schema.jsonc). */
export const MEDIA_FIELD_NAMES = collectMediaFieldNames(CASE_TYPE_SCHEMA)

const GMAIL_ATTACHMENT_URL_PATH =
  /^device\.apps\.gmail\[\d+\]\.attachments\[\d+\]\.url$/

/** True for generated or uploaded case assets that must exist before publish. */
export function isCaseAssetUrlField(path: string, field: string): boolean {
  return MEDIA_FIELD_NAMES.has(field) || GMAIL_ATTACHMENT_URL_PATH.test(path)
}

export function collectDashMediaUrls(
  value: unknown,
  basePath = '',
): ReturnType<typeof collectMediaUrlsBase> {
  return collectMediaUrlsBase(value, basePath, isCaseAssetUrlField)
}

/** schema.jsonc text for generators / script-check (comments kept). */
export function formatCaseSchemaForPrompt(): string {
  return CASE_SCHEMA_SOURCE.trimEnd()
}

// ── Validate (current schema only) ─────────────────────────────────────────

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function push(errors: ValidationIssue[], path: string, message: string): void {
  errors.push({ path, message })
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function hasNonEmptyArray(value: unknown): boolean {
  return Array.isArray(value) && value.length > 0
}

function hasPhotosPayload(value: unknown): boolean {
  if (!isRecord(value)) return false
  return (
    hasNonEmptyArray(value.photos) ||
    hasNonEmptyArray(value.screenshots) ||
    hasNonEmptyArray(value.videos) ||
    hasNonEmptyArray(value.albums) ||
    hasNonEmptyArray(value.hidden) ||
    hasNonEmptyArray(value.recently_deleted)
  )
}

function validateProtectedPhotos(
  errors: ValidationIssue[],
  value: unknown,
): void {
  if (!isRecord(value)) return
  const hasProtectedPhotos =
    hasNonEmptyArray(value.hidden) || hasNonEmptyArray(value.recently_deleted)
  const passcode = value.passcode
  if (hasProtectedPhotos && !nonEmptyString(passcode)) {
    push(
      errors,
      'device.apps.photos.passcode',
      'passcode is required when hidden or recently_deleted photos are present',
    )
  }
  if (passcode !== undefined &&
      (typeof passcode !== 'string' || !/^\d{6}$/.test(passcode))) {
    push(
      errors,
      'device.apps.photos.passcode',
      'passcode must be a 6-digit string',
    )
  }
}

const CLOCK_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/

function validateClock(errors: ValidationIssue[], value: unknown): void {
  if (value === undefined || !isRecord(value) || !Array.isArray(value.alarms)) {
    return
  }

  let wakeAlarmCount = 0
  value.alarms.forEach((alarm, index) => {
    if (!isRecord(alarm)) return
    const at = `device.apps.clock.alarms[${index}]`

    if (typeof alarm.time === 'string' && !CLOCK_TIME_RE.test(alarm.time)) {
      push(errors, `${at}.time`, 'must use 24-hour HH:mm')
    }

    if (alarm.kind === 'wake') wakeAlarmCount += 1

    if (alarm.repeat_days === undefined || !Array.isArray(alarm.repeat_days)) {
      return
    }
    if (alarm.repeat_days.length === 0) {
      push(errors, `${at}.repeat_days`, 'must be omitted when the alarm does not repeat')
      return
    }
    const days = alarm.repeat_days.filter(
      (day): day is number => typeof day === 'number' && Number.isInteger(day),
    )
    if (
      alarm.repeat_days.some(
        (day) =>
          typeof day !== 'number' ||
          !Number.isInteger(day) ||
          day < 0 ||
          day > 6,
      )
    ) {
      push(errors, `${at}.repeat_days`, 'days must be whole numbers from 0 to 6')
    }
    if (new Set(days).size !== days.length) {
      push(errors, `${at}.repeat_days`, 'days must be unique')
    }
  })

  if (wakeAlarmCount > 1) {
    push(errors, 'device.apps.clock.alarms', 'must include at most one wake alarm')
  }
}

function hasPhonePayload(value: unknown): boolean {
  if (!isRecord(value)) return false
  return hasNonEmptyArray(value.calls) || hasNonEmptyArray(value.voicemails)
}

function validateRequiredDeviceApps(
  errors: ValidationIssue[],
  apps: Record<string, unknown>,
): void {
  for (const key of REQUIRED_DEVICE_APP_KEYS) {
    const path = `device.apps.${key}`
    const value = apps[key]
    if (value === undefined) {
      push(errors, path, `required core app "${key}" is missing`)
      continue
    }
    if (key === 'photos') {
      if (!isRecord(value)) {
        push(errors, path, 'photos must be an object with media arrays')
      } else if (!hasPhotosPayload(value)) {
        push(
          errors,
          path,
          'photos must include non-empty photos, screenshots, videos, or albums',
        )
      }
      continue
    }
    if (key === 'phone') {
      if (!isRecord(value)) {
        push(errors, path, 'phone must be an object with calls and/or voicemails')
      } else if (!hasPhonePayload(value)) {
        push(errors, path, 'phone must include non-empty calls or voicemails')
      }
      continue
    }
    if (key === 'maps') {
      if (!isRecord(value)) {
        push(errors, path, 'maps must be an object with visits')
      } else if (!hasNonEmptyArray(value.visits)) {
        push(errors, path, 'maps must include non-empty visits')
      }
      continue
    }
    if (key === 'safari_bookmarks') {
      if (!isRecord(value)) {
        push(errors, path, 'safari_bookmarks must be an object with items')
      } else if (!hasNonEmptyArray(value.items)) {
        push(errors, path, 'safari_bookmarks must include non-empty items')
      }
      continue
    }
    if (!hasNonEmptyArray(value)) {
      push(
        errors,
        path,
        `required core app "${key}" must be a non-empty array of mock life data`,
      )
    }
  }
}

function validateBubbles(
  errors: ValidationIssue[],
  path: string,
  value: unknown,
  opts?: { min?: number },
): void {
  if (!Array.isArray(value)) {
    push(errors, path, 'must be an array of { body } bubbles')
    return
  }
  const min = opts?.min ?? 1
  if (value.length < min) {
    push(errors, path, `must have at least ${min} bubble(s)`)
    return
  }
  value.forEach((bubble, i) => {
    if (!isRecord(bubble)) {
      push(errors, `${path}[${i}]`, 'must be an object')
      return
    }
    if (!nonEmptyString(bubble.body)) {
      push(errors, `${path}[${i}].body`, 'body must be a non-empty string')
    }
  })
}

function validatePuzzleHints(
  errors: ValidationIssue[],
  base: string,
  step: Record<string, unknown>,
): void {
  if ('tip' in step) {
    push(
      errors,
      `${base}.tip`,
      'tip is removed; use hints.level_1 and hints.level_2',
    )
  }
  if (!isRecord(step.hints)) {
    push(errors, `${base}.hints`, 'hints must be an object')
    return
  }
  if (!nonEmptyString(step.hints.level_1)) {
    push(
      errors,
      `${base}.hints.level_1`,
      'hints.level_1 must be a non-empty string',
    )
  }
  if (!nonEmptyString(step.hints.level_2)) {
    push(
      errors,
      `${base}.hints.level_2`,
      'hints.level_2 must be a non-empty string',
    )
  }
}

function validateWhatsApp(
  errors: ValidationIssue[],
  value: unknown,
  peopleByName: Map<string, Record<string, unknown>>,
): void {
  if (value === undefined || !Array.isArray(value)) return

  const groups = new Map<
    string,
    { name: string; avatarUrl: string; memberKey: string }
  >()

  value.forEach((message, index) => {
    if (!isRecord(message)) return
    const at = `device.apps.whatsapp[${index}]`
    if (message.body === undefined && message.image_url === undefined) {
      push(errors, at, 'WhatsApp messages require body, image_url, or both')
    }
    const groupFields = [
      'group_id',
      'group_name',
      'group_avatar_url',
      'group_members',
    ] as const
    const presentCount = groupFields.filter(
      (field) => message[field] !== undefined,
    ).length
    if (presentCount === 0) return
    if (presentCount !== groupFields.length) {
      push(
        errors,
        at,
        'group messages require group_id, group_name, group_avatar_url, and group_members',
      )
      return
    }

    if (!nonEmptyString(message.group_id)) {
      push(errors, `${at}.group_id`, 'must be a non-empty string')
      return
    }
    if (!nonEmptyString(message.group_name)) {
      push(errors, `${at}.group_name`, 'must be a non-empty string')
      return
    }
    if (!nonEmptyString(message.group_avatar_url)) {
      push(errors, `${at}.group_avatar_url`, 'must be a non-empty media URL')
      return
    }
    if (!Array.isArray(message.group_members)) return

    const members = message.group_members.filter(isRecord)
    const memberNames = members
      .map((member) => member.name)
      .filter(nonEmptyString)
    const uniqueNames = new Set(memberNames)
    if (uniqueNames.size < 3) {
      push(errors, `${at}.group_members`, 'group chats require at least 3 members')
    }
    if (uniqueNames.size !== memberNames.length) {
      push(errors, `${at}.group_members`, 'group members must be unique')
    }
    members.forEach((member, memberIndex) => {
      const memberAt = `${at}.group_members[${memberIndex}]`
      if (!nonEmptyString(member.name)) return
      if (!peopleByName.has(member.name)) {
        push(
          errors,
          `${memberAt}.name`,
          `member "${member.name}" not found in canon.people`,
        )
      }
      if (!nonEmptyString(member.nickname)) {
        push(errors, `${memberAt}.nickname`, 'must be a non-empty string')
      }
      if (!nonEmptyString(member.avatar_url)) {
        push(errors, `${memberAt}.avatar_url`, 'must be a non-empty media URL')
      }
    })
    if (nonEmptyString(message.sender) && !uniqueNames.has(message.sender)) {
      push(errors, `${at}.sender`, 'group message sender must be a group member')
    }
    if (message.receiver !== message.group_name) {
      push(errors, `${at}.receiver`, 'group message receiver must equal group_name')
    }

    const memberKey = JSON.stringify(
      members
        .map((member) => ({
          name: member.name,
          nickname: member.nickname,
          avatar_url: member.avatar_url,
        }))
        .sort((a, b) => String(a.name).localeCompare(String(b.name))),
    )
    const existing = groups.get(message.group_id)
    if (
      existing &&
      (existing.name !== message.group_name ||
        existing.avatarUrl !== message.group_avatar_url ||
        existing.memberKey !== memberKey)
    ) {
      push(
        errors,
        `${at}.group_id`,
        'all messages with one group_id must share group_name, group_avatar_url, and group_members',
      )
    } else if (!existing) {
      groups.set(message.group_id, {
        name: message.group_name,
        avatarUrl: message.group_avatar_url,
        memberKey,
      })
    }
  })
}

function validateInstagramMessages(
  errors: ValidationIssue[],
  value: unknown,
): void {
  if (value === undefined || !isRecord(value) || !Array.isArray(value.threads)) {
    return
  }
  if (value.threads.length === 0) {
    push(errors, 'device.apps.instagram_messages.threads', 'must be a non-empty array')
    return
  }

  const usernames = new Set<string>()
  value.threads.forEach((thread, threadIndex) => {
    if (!isRecord(thread)) return
    const at = `device.apps.instagram_messages.threads[${threadIndex}]`
    if (nonEmptyString(thread.participant_username)) {
      const username = thread.participant_username
        .trim()
        .replace(/^@/, '')
        .toLowerCase()
      if (usernames.has(username)) {
        push(errors, `${at}.participant_username`, 'participant usernames must be unique')
      }
      usernames.add(username)
    }
    if (!Array.isArray(thread.messages) || thread.messages.length === 0) {
      push(errors, `${at}.messages`, 'must be a non-empty array')
      return
    }

    let previousSentAt: number | undefined
    thread.messages.forEach((message, messageIndex) => {
      if (!isRecord(message)) return
      const messageAt = `${at}.messages[${messageIndex}]`
      if (!nonEmptyString(message.body) && !nonEmptyString(message.image_url)) {
        push(errors, messageAt, 'Instagram messages require body, image_url, or both')
      }
      const sentAt =
        typeof message.sent_at === 'string'
          ? Date.parse(message.sent_at)
          : Number.NaN
      if (previousSentAt !== undefined && sentAt < previousSentAt) {
        push(errors, `${at}.messages`, 'messages must be chronological')
      }
      if (Number.isFinite(sentAt)) previousSentAt = sentAt
    })
  })
}

function validateAirbnb(errors: ValidationIssue[], value: unknown): void {
  if (value === undefined) return
  if (!isRecord(value)) {
    push(errors, 'device.apps.airbnb', 'must be an object')
    return
  }

  if (value.map_url !== undefined) {
    const issue = formatError('media_url', value.map_url)
    if (issue) push(errors, 'device.apps.airbnb.map_url', issue)
  }

  if (value.trips === undefined) return
  if (!Array.isArray(value.trips)) {
    push(errors, 'device.apps.airbnb.trips', 'must be an array')
    return
  }

  value.trips.forEach((trip, index) => {
    const at = `device.apps.airbnb.trips[${index}]`
    if (!isRecord(trip)) {
      push(errors, at, 'must be an object')
      return
    }

    for (const field of ['check_in_at', 'check_out_at']) {
      const issue = formatError('datetime', trip[field])
      if (issue) push(errors, `${at}.${field}`, issue)
    }

    if (
      typeof trip.check_in_at === 'string' &&
      typeof trip.check_out_at === 'string' &&
      trip.check_out_at < trip.check_in_at
    ) {
      push(errors, `${at}.check_out_at`, 'must not be earlier than check_in_at')
    }

    if (trip.image_url !== undefined) {
      const issue = formatError('media_url', trip.image_url)
      if (issue) push(errors, `${at}.image_url`, issue)
    }

    for (const field of ['id', 'location', 'place', 'host_name']) {
      if (trip[field] !== undefined && !nonEmptyString(trip[field])) {
        push(errors, `${at}.${field}`, 'expected a non-empty string')
      }
    }

    if (trip.location === undefined && trip.place === undefined) {
      push(errors, at, 'needs location (canon.world.locations name) or place')
    }
  })
}

function validatePayPal(
  errors: ValidationIssue[],
  value: unknown,
  supportsPayPalIncoming = true,
): void {
  if (value === undefined) return
  if (!isRecord(value)) {
    push(errors, 'device.apps.paypal', 'must be an object')
    return
  }

  if (typeof value.balance !== 'number' || !Number.isFinite(value.balance)) {
    push(errors, 'device.apps.paypal.balance', 'must be a finite number')
  }

  if (!Array.isArray(value.orders)) {
    push(errors, 'device.apps.paypal.orders', 'must be a non-empty array')
    return
  }
  if (value.orders.length === 0) {
    push(errors, 'device.apps.paypal.orders', 'must be a non-empty array')
    return
  }

  value.orders.forEach((order, index) => {
    const at = `device.apps.paypal.orders[${index}]`
    if (!isRecord(order)) {
      push(errors, at, 'must be an object')
      return
    }

    if (!nonEmptyString(order.merchant)) {
      push(errors, `${at}.merchant`, 'must be a non-empty string')
    }

    const paidAtIssue = formatError('datetime', order.paid_at)
    if (paidAtIssue) push(errors, `${at}.paid_at`, paidAtIssue)

    if (typeof order.amount !== 'number' || !Number.isFinite(order.amount) || order.amount < 0) {
      push(errors, `${at}.amount`, 'must be a finite number greater than or equal to 0')
    }

    if (order.direction === 'incoming' && !supportsPayPalIncoming) {
      push(
        errors,
        `${at}.direction`,
        `incoming PayPal orders require phone runtime ${PAYPAL_INCOMING_RUNTIME} or newer`,
      )
    }

    if (order.id !== undefined && !nonEmptyString(order.id)) {
      push(errors, `${at}.id`, 'expected a non-empty string')
    }
  })
}

function validateUber(errors: ValidationIssue[], value: unknown): void {
  if (value === undefined) return
  if (!isRecord(value)) {
    push(errors, 'device.apps.uber', 'must be an object')
    return
  }

  if (value.carts !== undefined) {
    if (!Array.isArray(value.carts)) {
      push(errors, 'device.apps.uber.carts', 'must be an array')
    } else {
      value.carts.forEach((cart, index) => {
        const at = `device.apps.uber.carts[${index}]`
        if (!isRecord(cart)) {
          push(errors, at, 'must be an object')
          return
        }
        if (!nonEmptyString(cart.merchant)) {
          push(errors, `${at}.merchant`, 'must be a non-empty string')
        }
        if (!nonEmptyString(cart.deliver_to)) {
          push(errors, `${at}.deliver_to`, 'must be a non-empty string')
        }
        if (
          typeof cart.items_count !== 'number' ||
          !Number.isInteger(cart.items_count) ||
          cart.items_count < 1
        ) {
          push(errors, `${at}.items_count`, 'must be a whole number of 1 or more')
        }
        if (typeof cart.total !== 'number' || !Number.isFinite(cart.total) || cart.total < 0) {
          push(errors, `${at}.total`, 'must be a finite number greater than or equal to 0')
        }
      })
    }
  }

  if (!Array.isArray(value.trips) || value.trips.length === 0) {
    push(errors, 'device.apps.uber.trips', 'must be a non-empty array')
    return
  }

  value.trips.forEach((trip, index) => {
    const at = `device.apps.uber.trips[${index}]`
    if (!isRecord(trip)) {
      push(errors, at, 'must be an object')
      return
    }

    if (trip.kind !== 'ride' && trip.kind !== 'order') {
      push(errors, `${at}.kind`, 'must be "ride" or "order"')
    }

    if (!nonEmptyString(trip.title)) {
      push(errors, `${at}.title`, 'must be a non-empty string')
    }

    const startedAtIssue = formatError('datetime', trip.started_at)
    if (startedAtIssue) push(errors, `${at}.started_at`, startedAtIssue)

    if (typeof trip.amount !== 'number' || !Number.isFinite(trip.amount) || trip.amount < 0) {
      push(errors, `${at}.amount`, 'must be a finite number greater than or equal to 0')
    }

    if (trip.items_count !== undefined) {
      if (trip.kind !== 'order') {
        push(errors, `${at}.items_count`, 'only orders carry an item count')
      } else if (!Number.isInteger(trip.items_count) || (trip.items_count as number) < 1) {
        push(errors, `${at}.items_count`, 'must be a whole number of 1 or more')
      }
    }
  })
}

/** Body blocks carry different fields per kind; the type schema cannot express that. */
function validateSafariPageBlock(
  errors: ValidationIssue[],
  at: string,
  block: unknown,
): void {
  if (!isRecord(block)) return

  const needsText = ['paragraph', 'heading', 'quote', 'callout']
  if (
    typeof block.kind === 'string' &&
    needsText.includes(block.kind) &&
    !nonEmptyString(block.text)
  ) {
    push(errors, `${at}.text`, `kind "${block.kind}" needs a non-empty text`)
  }

  if (block.kind === 'list') {
    if (!Array.isArray(block.items) || block.items.length === 0) {
      push(errors, `${at}.items`, 'kind "list" needs a non-empty items array')
    }
    if (block.text !== undefined) {
      push(errors, `${at}.text`, 'kind "list" carries items, not text')
    }
  } else if (block.items !== undefined) {
    push(errors, `${at}.items`, 'items belong to kind "list"')
  }

  if (block.attribution !== undefined && block.kind !== 'quote') {
    push(errors, `${at}.attribution`, 'attribution belongs to kind "quote"')
  }
}

const SEARCH_RESULT_COUNT = 3
const SAFARI_HISTORY_COUNT = 10
const SAFARI_BOOKMARK_MIN_COUNT = 10

function validateSafari(errors: ValidationIssue[], value: unknown): void {
  if (!Array.isArray(value)) return

  if (value.length !== SAFARI_HISTORY_COUNT) {
    push(
      errors,
      'device.apps.safari',
      `must hold exactly ${SAFARI_HISTORY_COUNT} history records`,
    )
  }

  value.forEach((entry, index) => {
    if (!isRecord(entry)) return
    const base = `device.apps.safari[${index}]`

    if (entry.search_results !== undefined) {
      if (
        !Array.isArray(entry.search_results) ||
        entry.search_results.length !== SEARCH_RESULT_COUNT
      ) {
        push(
          errors,
          `${base}.search_results`,
          `must hold exactly ${SEARCH_RESULT_COUNT} results`,
        )
      }
    }

    if (!isRecord(entry.page)) return
    const at = `${base}.page`
    const blocks = entry.page.blocks

    if (!Array.isArray(blocks) || blocks.length === 0) {
      push(errors, `${at}.blocks`, 'must be a non-empty array of body blocks')
      return
    }

    blocks.forEach((block, blockIndex) => {
      validateSafariPageBlock(errors, `${at}.blocks[${blockIndex}]`, block)
    })

    if (!blocks.some((block) => isRecord(block) && block.kind === 'paragraph')) {
      push(errors, `${at}.blocks`, 'needs at least one paragraph block')
    }
  })
}

function validateSafariBookmarks(
  errors: ValidationIssue[],
  value: unknown,
): void {
  if (value === undefined || !isRecord(value)) return

  for (const field of ['favorites_count', 'tab_group_favorites_count'] as const) {
    const count = value[field]
    if (typeof count !== 'number' || !Number.isInteger(count) || count < 0) {
      push(
        errors,
        `device.apps.safari_bookmarks.${field}`,
        'must be a non-negative whole number',
      )
    }
  }

  if (!Array.isArray(value.items)) return
  if (value.items.length < SAFARI_BOOKMARK_MIN_COUNT) {
    push(
      errors,
      'device.apps.safari_bookmarks.items',
      `must hold at least ${SAFARI_BOOKMARK_MIN_COUNT} bookmarks`,
    )
  }
}

/**
 * Validate a case package against schema.jsonc (structural) plus product rules.
 * pinKnownVersion: when set, checks that phone_runtime_version (if present)
 * is a known pin; callers pass isPhoneRuntimePin from validate.ts.
 */
export function validateAgainstSchema(
  input: unknown,
  options?: {
    isPhoneRuntimePin?: (value: string) => boolean
    supportsPayPalIncoming?: boolean
  },
): ValidationResult {
  const errors: ValidationIssue[] = []
  const isPin = options?.isPhoneRuntimePin

  if (!isRecord(input)) {
    return {
      ok: false,
      errors: [{ path: '', message: 'bundle must be a JSON object' }],
    }
  }

  // Structural type check from schema.jsonc
  errors.push(...validateAgainstTypeSchema(input, CASE_TYPE_SCHEMA))

  const allowedTopLevel = new Set([
    ...CASE_TOP_LEVEL_KEYS,
    ...CASE_OPTIONAL_TOP_LEVEL_KEYS,
  ])
  const allowedDeviceApps = new Set(DEVICE_APP_KEYS)

  for (const key of Object.keys(input)) {
    if (!allowedTopLevel.has(key)) {
      push(errors, key, `unknown top-level key "${key}"`)
    }
  }

  if ('phone_runtime_version' in input) {
    const pin = input.phone_runtime_version
    if (typeof pin === 'string' && pin.length > 0 && isPin && !isPin(pin)) {
      push(
        errors,
        'phone_runtime_version',
        `invalid phone_runtime_version "${pin}" (expected known version or "latest")`,
      )
    }
  }

  if (typeof input.id === 'string' && input.id.includes(':')) {
    push(errors, 'id', 'story id must not contain ":"')
  }

  let peopleByName = new Map<string, Record<string, unknown>>()
  let protagonistName: string | null = null
  let guideName: string | null = null
  let playerName: string | null = null

  if (isRecord(input.canon)) {
    if (!Array.isArray(input.canon.people)) {
      push(errors, 'canon.people', 'must be an array')
    } else {
      peopleByName = new Map()
      const protagonists: string[] = []
      const guides: string[] = []
      const players: string[] = []

      input.canon.people.forEach((p, i) => {
        if (!isRecord(p)) {
          push(errors, `canon.people[${i}]`, 'must be an object')
          return
        }
        if (!nonEmptyString(p.name)) {
          push(errors, `canon.people[${i}].name`, 'name must be a non-empty string')
          return
        }
        if (peopleByName.has(p.name)) {
          push(
            errors,
            `canon.people[${i}].name`,
            `duplicate person name "${p.name}"`,
          )
        }
        peopleByName.set(p.name, p)

        if (p.is_protagonist === true) protagonists.push(p.name)
        if (p.is_guide === true) guides.push(p.name)
        if (p.is_player === true) players.push(p.name)

        if (typeof p.is_protagonist !== 'boolean') {
          push(
            errors,
            `canon.people[${i}].is_protagonist`,
            'is_protagonist is required boolean',
          )
        }
        if (typeof p.is_guide !== 'boolean') {
          push(
            errors,
            `canon.people[${i}].is_guide`,
            'is_guide is required boolean',
          )
        }
        if (typeof p.is_player !== 'boolean') {
          push(
            errors,
            `canon.people[${i}].is_player`,
            'is_player is required boolean',
          )
        }
      })

      if (protagonists.length !== 1) {
        push(
          errors,
          'canon.people',
          `exactly one is_protagonist=true required, found ${protagonists.length}`,
        )
      } else {
        protagonistName = protagonists[0] ?? null
      }
      if (guides.length !== 1) {
        push(
          errors,
          'canon.people',
          `exactly one is_guide=true required, found ${guides.length}`,
        )
      } else {
        guideName = guides[0] ?? null
      }
      if (players.length !== 1) {
        push(
          errors,
          'canon.people',
          `exactly one is_player=true required, found ${players.length}`,
        )
      } else {
        playerName = players[0] ?? null
      }
    }
    if (!Array.isArray(input.canon.relationships)) {
      push(errors, 'canon.relationships', 'must be an array')
    }
    if (!isRecord(input.canon.world)) {
      push(errors, 'canon.world', 'must be an object')
    }
  }

  if (isRecord(input.game)) {
    if (!isRecord(input.game.cast)) {
      push(errors, 'game.cast', 'must be an object')
    } else {
      const cast = input.game.cast
      const fields = ['protagonist', 'guide', 'player'] as const
      for (const field of fields) {
        const value = cast[field]
        if (!nonEmptyString(value)) {
          push(errors, `game.cast.${field}`, `${field} must be a non-empty string`)
          continue
        }
        if (!peopleByName.has(value)) {
          push(
            errors,
            `game.cast.${field}`,
            `${field} "${value}" not found in canon.people`,
          )
        }
      }

      if (
        nonEmptyString(cast.protagonist) &&
        protagonistName !== null &&
        cast.protagonist !== protagonistName
      ) {
        push(
          errors,
          'game.cast.protagonist',
          `must match the person with is_protagonist=true (${protagonistName})`,
        )
      }
      if (
        nonEmptyString(cast.guide) &&
        guideName !== null &&
        cast.guide !== guideName
      ) {
        push(
          errors,
          'game.cast.guide',
          `must match the person with is_guide=true (${guideName})`,
        )
      }
      if (
        nonEmptyString(cast.player) &&
        playerName !== null &&
        cast.player !== playerName
      ) {
        push(
          errors,
          'game.cast.player',
          `must match the person with is_player=true (${playerName})`,
        )
      }
    }

    if (!Array.isArray(input.game.guideSteps)) {
      push(errors, 'game.guideSteps', 'must be an array')
    } else {
      input.game.guideSteps.forEach((step, i) => {
        const base = `game.guideSteps[${i}]`
        if (!isRecord(step)) {
          push(errors, base, 'must be an object')
          return
        }
        if (step.kind === 'guide') {
          validateBubbles(errors, `${base}.messages`, step.messages)
        } else if (step.kind === 'choice') {
          if (!Array.isArray(step.choices)) {
            push(errors, `${base}.choices`, 'must be an array of exactly 2 options')
          } else if (step.choices.length !== 2) {
            push(
              errors,
              `${base}.choices`,
              `exactly 2 choices required, found ${step.choices.length}`,
            )
          } else {
            step.choices.forEach((choice, ci) => {
              const cpath = `${base}.choices[${ci}]`
              if (!isRecord(choice)) {
                push(errors, cpath, 'must be an object')
                return
              }
              if (!nonEmptyString(choice.label)) {
                push(errors, `${cpath}.label`, 'label must be a non-empty string')
              }
              validateBubbles(errors, `${cpath}.reply`, choice.reply)
            })
          }
          if (step.after !== undefined) {
            validateBubbles(errors, `${base}.after`, step.after, { min: 1 })
          }
        } else {
          push(errors, `${base}.kind`, 'kind must be "guide" or "choice"')
        }
      })
    }

    if (!Array.isArray(input.game.puzzleSteps)) {
      push(errors, 'game.puzzleSteps', 'must be an array')
    } else {
      input.game.puzzleSteps.forEach((step, i) => {
        const base = `game.puzzleSteps[${i}]`
        if (!isRecord(step)) {
          push(errors, base, 'must be an object')
          return
        }
        if (typeof step.is_free !== 'boolean') {
          push(errors, `${base}.is_free`, 'is_free is required boolean')
        }
        if (!nonEmptyString(step.question)) {
          push(errors, `${base}.question`, 'question must be a non-empty string')
        }
        if (!nonEmptyString(step.correct_answer)) {
          push(
            errors,
            `${base}.correct_answer`,
            'correct_answer must be a non-empty string',
          )
        }
        validateBubbles(errors, `${base}.error_reply`, step.error_reply)
        validatePuzzleHints(errors, base, step)
      })
    }

    if (!isRecord(input.game.closing)) {
      push(errors, 'game.closing', 'must be an object')
    } else {
      for (const field of ['statement', 'truth_review'] as const) {
        const value = input.game.closing[field]
        if (typeof value !== 'string' || value.trim().length === 0) {
          push(
            errors,
            `game.closing.${field}`,
            `${field} must be a non-empty string`,
          )
        }
      }
    }
  }

  if (isRecord(input.device)) {
    const seed = input.device.home_screen_layout_seed
    if (typeof seed !== 'string' || seed.trim().length === 0) {
      push(
        errors,
        'device.home_screen_layout_seed',
        'home_screen_layout_seed must be a non-empty string',
      )
    }

    if (!isRecord(input.device.atmosphere)) {
      push(errors, 'device.atmosphere', 'must be an object')
    }

    if (!isRecord(input.device.apps)) {
      push(errors, 'device.apps', 'must be an object')
    } else {
      for (const key of Object.keys(input.device.apps)) {
        if (!allowedDeviceApps.has(key)) {
          push(errors, `device.apps.${key}`, `unknown app key "${key}"`)
        }
      }
      validateAirbnb(errors, input.device.apps.airbnb)
      validatePayPal(
        errors,
        input.device.apps.paypal,
        options?.supportsPayPalIncoming ?? true,
      )
      validateUber(errors, input.device.apps.uber)
      validateSafari(errors, input.device.apps.safari)
      validateSafariBookmarks(errors, input.device.apps.safari_bookmarks)
      validateProtectedPhotos(errors, input.device.apps.photos)
      validateClock(errors, input.device.apps.clock)
      validateWhatsApp(errors, input.device.apps.whatsapp, peopleByName)
      validateInstagramMessages(errors, input.device.apps.instagram_messages)
      validateRequiredDeviceApps(errors, input.device.apps)
    }
  }

  if (isRecord(input.canon) && Array.isArray(input.canon.people)) {
    let withPhone = 0
    for (const person of input.canon.people) {
      if (!isRecord(person) || !isRecord(person.contact)) continue
      if (nonEmptyString(person.contact.phone)) withPhone += 1
    }
    if (withPhone < 2) {
      push(
        errors,
        'canon.people',
        'contacts require at least 2 people with contact.phone (mock life data)',
      )
    }
  }

  return { ok: errors.length === 0, errors }
}
